import axios from 'axios';
import logger from './logger.js';
import { getToken, removeToken, removeUser } from './auth.js';

// Environment configuration
const API_URL = import.meta.env.VITE_API_URL;
const IS_PRODUCTION = import.meta.env.PROD;
const IS_DEVELOPMENT = import.meta.env.DEV;

// Enhanced axios configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Production timeout settings
  timeout: IS_PRODUCTION ? 10000 : 30000, // 10s in prod, 30s in dev
  // Enable credentials for secure cookie handling
  withCredentials: true, // Always enable credentials for CORS
});

// Request interceptor to add JWT token and logging
api.interceptors.request.use(
  (config) => {
    const startTime = Date.now();
    config.metadata = { startTime };
    
    // Add JWT token
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request
    logger.apiRequest(config.method, config.url, config.data);
    
    return config;
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and logging
api.interceptors.response.use(
  (response) => {
    const duration = Date.now() - response.config.metadata.startTime;
    logger.apiResponse(response.config.method, response.config.url, response.status, response.data);
    logger.performance(`${response.config.method.toUpperCase()} ${response.config.url}`, duration);
    return response;
  },
  (error) => {
    const duration = error.config?.metadata ? Date.now() - error.config.metadata.startTime : 0;
    
    // Log error response
    if (error.response) {
      logger.apiResponse(error.config.method, error.config.url, error.response.status, error.response.data);
      logger.performance(`${error.config.method.toUpperCase()} ${error.config.url}`, duration);
    }
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      logger.auth('Token expired or invalid', false);
      removeToken();
      removeUser();
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      logger.auth('Access forbidden', false);
    } else if (error.response?.status >= 500) {
      logger.error('Server error:', error.response.data);
    } else if (error.code === 'ECONNABORTED') {
      logger.error('Request timeout:', error.message);
    } else if (!error.response) {
      logger.error('Network error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Normalize any axios/backend error into a plain string suitable for rendering.
// Handles all three response shapes our backend uses:
//   { error: 'msg' }              (whatsapp controllers)
//   { error: { message: 'msg' } } (global error handler in middlewares/error.js)
//   { message: 'msg' }            (some legacy handlers)
export function getApiErrorMessage(err, fallback = 'Request failed') {
  const data = err?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.error?.message === 'string') return data.error.message;
  if (typeof data?.message === 'string') return data.message;
  if (typeof err?.message === 'string') return err.message;
  return fallback;
}

// API endpoints
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  me: () => api.get('/auth/me'),
};

export const healthAPI = {
  live: () => api.get('/health/live'),
  ready: () => api.get('/health/ready'),
};

export const companiesAPI = {
  getAll: async (params) => {
    const companiesData = await api.get('/companies', { params });
    console.log('Companies Api data:', companiesData);
    return companiesData;
  },
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

export const designationsAPI = {
  getAll: async (params) => {
    const designationsData = await api.get('/designations', { params });
    console.log('Designations Api data:', designationsData);
    return designationsData;
  },
  getAllSimple: async () => {
    const designationsData = await api.get('/designations/all');
    console.log('Designations Simple Api data:', designationsData);
    return designationsData;
  },
  getById: (id) => api.get(`/designations/${id}`),
  create: (data) => api.post('/designations', data),
  update: (id, data) => api.put(`/designations/${id}`, data),
  delete: (id) => api.delete(`/designations/${id}`),
};

export const ordersAPI = {
  getAll: async (params) => {
    const response = await api.get('/orders', { params });
    return response.data;
  },
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  acknowledge: (id) => api.post(`/orders/${id}/acknowledge`),
  
  // Document management
  uploadPdf: (formData) => api.post('/orders/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadPdf: (id) => api.get(`${id}`),
  getStats: (params = {}) => api.get('/orders/stats', { params }),
  
  // Categories management
  categories: {
    getAll: (params = {}) => api.get('/orders/categories', { params }),
    getById: (id) => api.get(`/orders/categories/${id}`),
    create: (data) => api.post('/orders/categories', data),
    update: (id, data) => api.put(`/orders/categories/${id}`, data),
    delete: (id) => api.delete(`/orders/categories/${id}`),
  },
  
  // Subcategories management
  subcategories: {
    getAll: (categoryId, params = {}) => api.get(`/orders/categories/${categoryId}/subcategories`, { params }),
    getById: (id) => api.get(`/orders/subcategories/${id}`),
    create: (data) => api.post('/orders/subcategories', data),
    update: (id, data) => api.put(`/orders/subcategories/${id}`, data),
    delete: (id) => api.delete(`/orders/subcategories/${id}`),
  },
};

export const circularsAPI = {
  getAll: async (params) => {
    const response = await api.get('/circulars', { params });
    return response.data;
  },
  getById: (id) => api.get(`/circulars/${id}`),
  create: (data) => api.post('/circulars', data),
  update: (id, data) => api.put(`/circulars/${id}`, data),
  delete: (id) => api.delete(`/circulars/${id}`),
  acknowledge: (id) => api.post(`/circulars/${id}/acknowledge`),
  
  // Document management
  uploadPdf: (formData) => api.post('/circulars/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadPdf: (id) => api.get(`/circulars/${id}/download`),
  getStats: (params = {}) => api.get('/circulars/stats', { params }),
  
  // Category management (using orders module endpoints for now)
  categories: {
    getAll: (filters = {}) => api.get('/circulars/categories', { params: filters }),
    getById: (id) => api.get(`/circulars/categories/${id}`),
    create: (data) => api.post('/circulars/categories', data),
    update: (id, data) => api.put(`/circulars/categories/${id}`, data),
    delete: (id) => api.delete(`/circulars/categories/${id}`),
  },
  
  // Subcategory management (using circulars module endpoints for now)
  subcategories: {
    getAll: (categoryId) => api.get(`/circulars/categories/${categoryId}/subcategories`),
    getById: (id) => api.get(`/circulars/subcategories/${id}`),
    create: (data) => api.post('/circulars/subcategories', data),
    update: (id, data) => api.put(`/circulars/subcategories/${id}`, data),
    delete: (id) => api.delete(`/circulars/subcategories/${id}`),
  },
};

export const governmentDecisionsAPI = {
  getAll: async (params) => {
    const response = await api.get('/government-decisions', { params });
    return response.data;
  },
  getById: (id) => api.get(`/government-decisions/${id}`),
  create: (data) => api.post('/government-decisions', data),
  update: (id, data) => api.put(`/government-decisions/${id}`, data),
  delete: (id) => api.delete(`/government-decisions/${id}`),
  acknowledge: (id) => api.post(`/government-decisions/${id}/acknowledge`),
  
  // Document management
  uploadPdf: (formData) => api.post('/government-decisions/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadPdf: (id) => api.get(`/government-decisions/${id}/download`),
  getStats: (params = {}) => api.get('/government-decisions/stats', { params }),
  
  // Categories management
  categories: {
    getAll: (filters = {}) => api.get('/government-decisions/categories', { params: filters }),
    getById: (id) => api.get(`/government-decisions/categories/${id}`),
    create: (data) => api.post('/government-decisions/categories', data),
    update: (id, data) => api.put(`/government-decisions/categories/${id}`, data),
    delete: (id) => api.delete(`/government-decisions/categories/${id}`),
  },
  
  // Subcategories management
  subcategories: {
    getAll: (categoryId) => api.get(`/government-decisions/categories/${categoryId}/subcategories`),
    getById: (id) => api.get(`/government-decisions/subcategories/${id}`),
    create: (data) => api.post('/government-decisions/subcategories', data),
    update: (id, data) => api.put(`/government-decisions/subcategories/${id}`, data),
    delete: (id) => api.delete(`/government-decisions/subcategories/${id}`),
  },
};

export const kpisAPI = {
  getDaily: (params) => {
    const kpisResponse = api.get('/kpis/daily', { params });
  console.log('Kpis Api data:', kpisResponse);
  return kpisResponse;
  },
  getExec: (params) => {
    const kpisResponse = api.get('/kpis/exec', { params });
    console.log('Kpis Api data:', kpisResponse);
    return kpisResponse;
  },
  getTrends: (params) => {
    const kpisResponse = api.get('/kpis/trends', { params });
    console.log('Kpis Api data:', kpisResponse);
    return kpisResponse;
  },
};

export const searchAPI = {
  global: (query, params) => api.get('/search', { params: { q: query, ...params } }),
};

export const usersAPI = {
  // getAll: (params) => api.get('/users', { params }),
  getAll: async (params) => {
    return api.get('/users', { params });
  },

  getById: (id) => api.get(`/users/${id}`),
  create: async (data) => {
    console.log('Creating user with data:', data);
    try {
      const createResponse = await api.post('/users', data);
      console.log('Create User Api data:', createResponse);
      return createResponse;
    } catch (error) {
      console.error('Create User Error:', error);
      console.error('Error details:', error.response?.data);
      throw error;
    }
  },
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  updateProfile: (data) => api.put('/users/profile/me', data),
  changePassword: (data) => api.put('/users/profile/password', data),
  resetPassword: (userId, data) => api.put(`/users/${userId}/reset-password`, data),
  getRoles: async () => {
    const rolesResponse = await api.get('/users/roles');
    console.log('Roles Api data:', rolesResponse);
    return rolesResponse;
  },
};

export const auditAPI = {
  getLogs: (params) => api.get('/audit/logs', { params }),
};

export const appointmentsAPI = {
  getAll: (params) => api.get('/appointments', { params }),
  getById: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  reschedule: (id, data) => api.post(`/appointments/${id}/reschedule`, data),
  getHolidays: (params) => api.get('/appointments/holidays/all', { params }),
  createHoliday: (data) => api.post('/appointments/holidays', data),
  rescheduleByHolidayDate: (holidayDate) => api.post(`/appointments/holidays/${holidayDate}/reschedule`),
  getLeaveTypes: (params) => api.get('/appointments/leave-types/all', { params }),
  generateRegister: (data) => api.post('/appointments/register/print', data),
  findUserByPhone: (phoneNumber) => api.get('/appointments/users/find-by-phone', { params: { phone_number: phoneNumber } }),
  // Simple Leave Appointment endpoints
  createLeaveAppointment: (data) => api.post('/appointments/leave', data),
  updateLeaveAppointment: (id, data) => api.put(`/appointments/leave/${id}`, data),
  
  // Enhanced Leave Appointment APIs
  leave: {
    create: (data) => api.post('/appointments/leave', data),
    getDetails: (id) => api.get(`/appointments/leave/${id}`),
    processClerk: (id, data) => api.post(`/appointments/${id}/process-clerk`, data),
    addRemark: (id, data) => api.post(`/appointments/${id}/remarks`, data),
    getRemarks: (id) => api.get(`/appointments/${id}/remarks`),
    createFinalOrder: (id, data) => api.post(`/appointments/${id}/final-order`, data),
    getFinalOrders: (id) => api.get(`/appointments/${id}/final-orders`),
    addRewardsPunishments: (id, data) => api.post(`/appointments/${id}/rewards-punishments`, data),
    addSevaAbhilekh: (id, data) => api.post(`/appointments/${id}/seva-abhilekh`, data),
    updateWorkflow: (id, data) => api.put(`/appointments/${id}/workflow`, data),
    getByWorkflowStage: (stage, params) => api.get(`/appointments/workflow/${stage}`, { params }),
    assignReviewer: (id, data) => api.post(`/appointments/${id}/assign-reviewer`, data),
    getStats: (params) => api.get('/appointments/leave/stats/summary', { params }),
  }
};
export const formsAPI = {
  // Basic CRUD operations
  getAll: (filters = {}) => api.get('/forms', { params: filters }),
  getHierarchical: (filters = {}) => api.get('/forms/hierarchical', { params: filters }),
  getById: (id) => api.get(`/forms/${id}`),
  create: (data) => { 
    // Handle FormData for file uploads
    if (data instanceof FormData) {
      return api.post('/forms', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      });
    }
    return api.post('/forms', data);
  },
  update: (id, data) => api.put(`/forms/${id}`, data),
  delete: (id) => api.delete(`/forms/${id}`),
  download: (id) => api.get(`/forms/${id}/download`),
  getStats: (id) => api.get(`/forms/${id}/stats`),
  getRecentDownloads: (limit = 10) => api.get('/forms/downloads/recent', { params: { limit } }),
  
  // Category tree for hierarchical display
  getCategoryTree: (params = {}) => api.get('/forms/categories/tree', { params }),
  
  // Hierarchical category management 
  categories: {
    getAll: (filters = {}) => api.get('/forms/categories', { params: filters }),
    getById: (id) => api.get(`/forms/categories/${id}`),
    create: (data) => api.post('/forms/categories', data),
    update: (id, data) => api.put(`/forms/categories/${id}`, data),
    delete: (id) => api.delete(`/forms/categories/${id}`),
  },
  
  subcategories: {
    getAll: (filters = {}) => api.get('/forms/subcategories', { params: filters }),
    getById: (id) => api.get(`/forms/subcategories/${id}`),
    create: (data) => api.post('/forms/subcategories', data),
    update: (id, data) => api.put(`/forms/subcategories/${id}`, data),
    delete: (id) => api.delete(`/forms/subcategories/${id}`),
  },
  
  subSubcategories: {
    getAll: (filters = {}) => api.get('/forms/sub-subcategories', { params: filters }),
    getById: (id) => api.get(`/forms/sub-subcategories/${id}`),
    create: (data) => api.post('/forms/sub-subcategories', data),
    update: (id, data) => api.put(`/forms/sub-subcategories/${id}`, data),
    delete: (id) => api.delete(`/forms/sub-subcategories/${id}`),
  },
  
  // WhatsApp integration - For WhatsApp bot consumption only
  whatsapp: {
    getHierarchicalMenu: (filters = {}) => api.get('/forms/whatsapp/hierarchical-menu', { params: filters }),
    handleHierarchicalNavigation: (selectionId, filters = {}) => api.get(`/forms/whatsapp/hierarchical/${selectionId}`, { params: filters }),
  },
};

export const whatsappAPI = {
  // Message management
  sendMessage: (data) => api.post('/whatsapp/send', data),
  getMessageHistory: (params = {}) => api.get('/whatsapp/messages', { params }),
  
  // Grievance tickets
  getGrievanceTickets: (params = {}) => api.get('/whatsapp/grievances', { params }),
  
  // Broadcast campaigns
  createBroadcastCampaign: (data) => api.post('/whatsapp/campaigns', data),
  getBroadcastCampaigns: (params = {}) => api.get('/whatsapp/campaigns', { params }),
  getBroadcastCampaign: (campaignId) => api.get(`/whatsapp/campaigns/${campaignId}`),
  sendBroadcastCampaign: (campaignId) => api.post(`/whatsapp/campaigns/${campaignId}/send`),
  getBroadcastStats: (campaignId) => api.get(`/whatsapp/campaigns/${campaignId}/stats`),
  
  // Selectable users for broadcast
  getSelectableUsers: (params = {}) => api.get('/whatsapp/selectable-users', { params }),

  // Template broadcasts — live fetch of APPROVED Meta templates + send
  getApprovedTemplates: () => api.get('/whatsapp/templates/approved'),
  sendTemplateBroadcast: (data) => api.post('/whatsapp/templates/send', data),
  
  // Settings
  updateSettings: (data) => api.put('/whatsapp/settings', data),
  
  // Statistics
  getStats: (params = {}) => api.get('/whatsapp/stats', { params }),
};

export const whatsappAnalyticsAPI = {
  getCounts: (params = {}) => api.get('/whatsapp-analytics', { params }),
};

export const payslipsAPI = {
  // CRUD operations
  getAll: (params = {}) => api.get('/salary-slip/salaryslips/all', { params }),
  getById: (id) => api.get(`/payslips/${id}`),
  create: (data) => api.post('/payslips', data),
  
  // WhatsApp delivery
  sendViaWhatsApp: (id) => api.post(`/payslips/${id}/send`),
  bulkSendViaWhatsApp: (data) => api.post('/payslips/bulk-send', data),
  
  // File operations
  download: (id) => api.get(`/payslips/${id}/download`),
  
  // Statistics and reports
  getStats: (params = {}) => api.get('/payslips/stats', { params }),
  getEligibleUsers: (params = {}) => api.get('/payslips/eligible-users', { params }),
  
  // Delivery status (webhook)
  updateDeliveryStatus: (data) => api.post('/payslips/delivery-status', data),
  // Bulk salary-slip files are large (1000+ page PDFs) and the server splits
  // them per employee, so override the global 10s prod timeout with 10 minutes
  // to match the backend's extended request timeout (see salarySlip/routes.js).
  uploadHtml: (formData) => api.post('/salary-slip/upload-html', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }),
  // Customer now delivers the combined salary slips as a single PDF (one slip
  // per page) instead of HTML; this splits it per employee server-side.
  uploadPdf: (formData) => api.post('/salary-slip/upload-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }),
  // Dry run: coverage breakdown for a combined PDF, writes nothing.
  previewPdf: (formData) => api.post('/salary-slip/preview-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }),
};

// Form 16 tax certificates. Admins upload a ZIP of ~1200 pre-split per-employee
// PDFs (named by PAN); employees fetch their own. The ZIP is large and extracted
// server-side, so upload/preview override the global prod timeout (10 min) to
// match the backend's extended request timeout (see form16/routes.js).
export const form16API = {
  // Employee: my own Form 16 certificates (download URLs fetched on demand).
  getMine: () => api.get('/form16/my'),
  // Admin: list all stored Form 16s (filter by financial_year / pan_no / company_id).
  getAll: (params = {}) => api.get('/form16/all', { params }),
  // Mint a fresh signed download URL for one certificate (ownership-checked).
  getDownloadLink: (id) => api.get(`/form16/${id}/link`),
  // Admin dry run: report match coverage for the ZIP, writes nothing. The server
  // stages the uploaded ZIP and returns `staged` so confirm needn't re-upload it.
  previewZip: (formData) => api.post('/form16/preview-zip', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }),
  // Admin commit. Pass FormData with a `staged` field (from previewZip) to avoid
  // re-uploading the archive; falls back to a fresh `zipFile` if not staged.
  uploadZip: (formData) => api.post('/form16/upload-zip', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }),
};



// Export all APIs  
export default api;